import { FC } from 'react';
import { Avatar, Col, Row } from 'antd';
import { CalendarRange } from 'lucide-react';
import { TbBinaryTree2, TbCategory2, TbCubeSpark, TbDatabase } from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { useMaps } from '@/hooks/useMaps';
import { useReportComponentDefinitions } from '@/hooks/useReportComponentDefinitions';
import { PlatformDatePicker, ReportComponentIcon } from '@/shared';
import DropdownButton from '@/shared/components/dropdowns/DropdownButton';
import { PlatformForm as Form } from '@/shared/components/forms';
import { useConnectedAccountStore, useReportComponentStore } from '@/stores';
import { ConnectedAccountType, ReportComponent } from '@/types';
import { DataSourceType, ReportComponentConfig } from '@/types/api/modules/business-intelligence';

import { parseDateRangePickerValue, updateDateRangeBoundary } from '../../../../helpers/dateRange';
import ReportComponentSelectionDropdown from './components/01-ReportComponentSelectionDropdown';
import DatasetSelectionDropdown from './components/02-DatasetSelectionDropdown';
import DatasetDefinitionSelectionDropdown from './components/03-DatasetDefinitionSelectionDropdown';
import DataSourceSelectionDropdown from './components/04-DataSourceSelectionDropdown';
import { useDatasetSchema } from './hooks';

interface OverviewProps {
    component: ReportComponent;
}

const safeDecodeURIComponent = (value?: string) => {
    try {
        return decodeURIComponent(value ?? '');
    } catch {
        return value ?? '';
    }
};

const parseConnectedAccountProviderId = (datasetKey?: string) => {
    const [prefix, providerId] = String(datasetKey ?? '')
        .split(':')
        .map(safeDecodeURIComponent);

    if (prefix !== 'connected-account' || !providerId) {
        return undefined;
    }

    return providerId;
};

const Overview: FC<OverviewProps> = ({ component }): JSX.Element => {
    const { token } = useAntDesignTheme();

    const { REPORT_COMPONENT_DEFINITIONS } = useReportComponentDefinitions();

    const { draftConfigurations } = useReportComponentStore();

    const draft: Partial<ReportComponentConfig> = draftConfigurations[component?.id as string];

    const def = REPORT_COMPONENT_DEFINITIONS[component.type];
    const caps = def.capabilities;

    const {
        integrationLabelToIconMap,
        reportComponentConfigurationDatasetSourceTypeIconMap,
        reportComponentConfigurationDatasetCategoryIconMap,
    } = useMaps();

    const { connectedAccounts } = useConnectedAccountStore();

    const selectedProviderId = draft?.dataSet?.providerId ?? parseConnectedAccountProviderId(draft?.dataSet?.key);
    const selected = connectedAccounts.find((o) => o.id === selectedProviderId);

    const { schema } = useDatasetSchema(draft as ReportComponentConfig, { includeCategory: false });
    const { schema: selectedDatasetSchema, isLoading: selectedDatasetSchemaLoading } = useDatasetSchema(
        draft as ReportComponentConfig,
    );

    const selectedDatasetDef = schema.datasets.find((dataset) => dataset.key === draft?.dataSet?.key);
    const selectedProviderDatasetDef = schema?.datasets?.find(
        (dataset) => parseConnectedAccountProviderId(dataset.key) === selectedProviderId,
    );
    const selectedConnectedAccountIconSrc = selected?.type
        ? integrationLabelToIconMap[selected.type as ConnectedAccountType]
        : undefined;
    const isThirdPartyDataset = draft?.dataSet?.type === DataSourceType.THIRD_PARTY;
    const isVantageDataBlock =
        isThirdPartyDataset &&
        Boolean(draft?.dataSet?.key) &&
        !String(draft?.dataSet?.key ?? '').startsWith('connected-account:');
    const selectedDataSourceLabel =
        selected?.nickname ??
        selected?.type ??
        (isVantageDataBlock ? selectedDatasetDef?.label : selectedProviderDatasetDef?.label) ??
        'Select Data Source';
    const isRegisteredDataset =
        draft?.dataSet?.type === DataSourceType.PLATFORM_FINANCIAL ||
        draft?.dataSet?.type === DataSourceType.PLATFORM_ECONOMIC ||
        draft?.dataSet?.type === DataSourceType.GLOBAL_TRADE ||
        draft?.dataSet?.type === DataSourceType.WORKSPACE_ANALYTICS;
    const showThirdPartyDatasetCategory = isThirdPartyDataset && Boolean(draft?.dataSet?.providerId);
    const overviewColumnSpan = 8;
    const startPeriod = parseDateRangePickerValue(draft?.query?.dateRange?.start);
    const endPeriod = parseDateRangePickerValue(draft?.query?.dateRange?.end);
    const dateRangeSupported = selectedDatasetSchema.time?.supported === true;
    const dateRangeDisabled = !draft?.dataSet?.key || selectedDatasetSchemaLoading || !dateRangeSupported;

    const updateDraft = useReportComponentStore((s) => s.updateDraftConfiguration);

    return (
        <>
            <div style={{ padding: '0 1rem' }}>
                <Row gutter={[16, 8]}>
                    <Col span={overviewColumnSpan}>
                        <Form.Item style={{ marginBottom: '0.5rem' }} label='Component Type'>
                            <ReportComponentSelectionDropdown component={component}>
                                <DropdownButton
                                    size='small'
                                    block
                                    style={{ justifyContent: 'flex-start', textAlign: 'left' }}
                                >
                                    <Row style={{ width: '100%' }} wrap={false} align={'middle'} gutter={[6, 0]}>
                                        <Col flex='none'>
                                            <ReportComponentIcon size={16} type={component.type} />
                                        </Col>
                                        <Col flex='auto' style={{ minWidth: 0, textAlign: 'left' }}>
                                            <span
                                                style={{
                                                    display: 'block',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                }}
                                            >
                                                {component?.type}
                                            </span>
                                        </Col>
                                    </Row>
                                </DropdownButton>
                            </ReportComponentSelectionDropdown>
                        </Form.Item>
                    </Col>
                    {caps.overview.dataSource && (
                        <Col span={overviewColumnSpan}>
                            <Form.Item style={{ marginBottom: '0.5rem' }} label='Dataset'>
                                <DatasetSelectionDropdown component={component}>
                                    <DropdownButton
                                        size='small'
                                        block
                                        style={{
                                            justifyContent: 'flex-start',
                                            textAlign: 'left',
                                        }}
                                    >
                                        <Row style={{ width: '100%' }} wrap={false} align={'middle'} gutter={[6, 0]}>
                                            <Col flex='none'>
                                                <Avatar
                                                    size={16}
                                                    shape='square'
                                                    style={{
                                                        backgroundColor: token.colorBgBase,
                                                        border: `1px solid ${token.colorBorder}`,
                                                    }}
                                                    icon={
                                                        reportComponentConfigurationDatasetSourceTypeIconMap[
                                                            draft?.dataSet?.type as DataSourceType
                                                        ] ?? (
                                                            <TbBinaryTree2
                                                                style={{ color: token.colorTextTertiary }}
                                                                size='75%'
                                                            />
                                                        )
                                                    }
                                                />
                                            </Col>
                                            <Col flex='auto' style={{ minWidth: 0, textAlign: 'left' }}>
                                                <span
                                                    style={{
                                                        display: 'block',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                    }}
                                                >
                                                    {draft?.dataSet?.type ?? 'Select Dataset'}
                                                </span>
                                            </Col>
                                        </Row>
                                    </DropdownButton>
                                </DatasetSelectionDropdown>
                            </Form.Item>
                        </Col>
                    )}
                    {caps.overview.dataSource && draft?.dataSet?.type && (
                        <Col span={overviewColumnSpan}>
                            <Form.Item
                                style={{ marginBottom: '0.5rem' }}
                                label={isRegisteredDataset ? 'Dataset Category' : 'Data Source'}
                            >
                                {isRegisteredDataset ? (
                                    <DatasetDefinitionSelectionDropdown component={component}>
                                        <DropdownButton
                                            size='small'
                                            block
                                            style={{
                                                justifyContent: 'flex-start',
                                                textAlign: 'left',
                                            }}
                                        >
                                            <Row
                                                style={{ width: '100%' }}
                                                wrap={false}
                                                align={'middle'}
                                                gutter={[6, 0]}
                                            >
                                                <Col flex='none'>
                                                    <Avatar
                                                        size={16}
                                                        shape='square'
                                                        style={{
                                                            backgroundColor: token.colorBgBase,
                                                            border: `1px solid ${token.colorBorder}`,
                                                        }}
                                                        icon={
                                                            reportComponentConfigurationDatasetCategoryIconMap[
                                                                (selectedDatasetDef?.icon ??
                                                                    selectedDatasetDef?.key) as string
                                                            ] ?? (
                                                                <TbCategory2
                                                                    style={{ color: token.colorTextTertiary }}
                                                                    size='75%'
                                                                />
                                                            )
                                                        }
                                                    />
                                                </Col>
                                                <Col flex='auto' style={{ minWidth: 0, textAlign: 'left' }}>
                                                    <span
                                                        style={{
                                                            display: 'block',
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                        }}
                                                    >
                                                        {selectedDatasetDef?.label ?? 'Select Category'}
                                                    </span>
                                                </Col>
                                            </Row>
                                        </DropdownButton>
                                    </DatasetDefinitionSelectionDropdown>
                                ) : (
                                    <DataSourceSelectionDropdown component={component}>
                                        <DropdownButton
                                            size='small'
                                            block
                                            style={{
                                                justifyContent: 'flex-start',
                                                textAlign: 'left',
                                            }}
                                        >
                                            <Row
                                                style={{ width: '100%' }}
                                                wrap={false}
                                                align={'middle'}
                                                gutter={[6, 0]}
                                            >
                                                <Col flex='none'>
                                                    <Avatar
                                                        size={16}
                                                        shape='square'
                                                        src={selectedConnectedAccountIconSrc}
                                                        style={{
                                                            backgroundColor: token.colorBgBase,
                                                            border: `1px solid ${token.colorBorder}`,
                                                        }}
                                                        icon={
                                                            isVantageDataBlock ? (
                                                                <TbCubeSpark
                                                                    style={{ color: token.colorTextTertiary }}
                                                                    size='75%'
                                                                />
                                                            ) : (
                                                                (reportComponentConfigurationDatasetCategoryIconMap[
                                                                    (selectedDatasetDef?.icon ??
                                                                        selectedDatasetDef?.key) as string
                                                                ] ??
                                                                (selectedConnectedAccountIconSrc ? undefined : (
                                                                    <TbDatabase
                                                                        style={{ color: token.colorTextTertiary }}
                                                                        size='75%'
                                                                    />
                                                                )))
                                                            )
                                                        }
                                                    />
                                                </Col>
                                                <Col flex='auto' style={{ minWidth: 0 }}>
                                                    <span
                                                        style={{
                                                            display: 'block',
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                            whiteSpace: 'nowrap',
                                                            verticalAlign: '-1px',
                                                        }}
                                                    >
                                                        {selectedDataSourceLabel}
                                                    </span>
                                                </Col>
                                            </Row>
                                        </DropdownButton>
                                    </DataSourceSelectionDropdown>
                                )}
                            </Form.Item>
                        </Col>
                    )}
                    {caps.overview.dataSource && showThirdPartyDatasetCategory && (
                        <Col span={overviewColumnSpan}>
                            <Form.Item style={{ marginBottom: '0.5rem' }} label='Dataset Category'>
                                <DatasetDefinitionSelectionDropdown component={component}>
                                    <DropdownButton
                                        size='small'
                                        block
                                        style={{
                                            justifyContent: 'flex-start',
                                            textAlign: 'left',
                                        }}
                                    >
                                        <Row style={{ width: '100%' }} wrap={false} align={'middle'} gutter={[6, 0]}>
                                            <Col flex='none'>
                                                <Avatar
                                                    size={16}
                                                    shape='square'
                                                    src={selectedConnectedAccountIconSrc}
                                                    style={{
                                                        backgroundColor: token.colorBgBase,
                                                        border: `1px solid ${token.colorBorder}`,
                                                    }}
                                                    icon={
                                                        selectedConnectedAccountIconSrc ? undefined : (
                                                            <TbCategory2
                                                                style={{ color: token.colorTextTertiary }}
                                                                size='75%'
                                                            />
                                                        )
                                                    }
                                                />
                                            </Col>
                                            <Col flex='auto' style={{ minWidth: 0, textAlign: 'left' }}>
                                                <span
                                                    style={{
                                                        display: 'block',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                    }}
                                                >
                                                    {selectedDatasetDef?.label ?? 'Select Category'}
                                                </span>
                                            </Col>
                                        </Row>
                                    </DropdownButton>
                                </DatasetDefinitionSelectionDropdown>
                            </Form.Item>
                        </Col>
                    )}
                </Row>
                {caps.overview.dateRange && (
                    <Row gutter={[16, 0]}>
                        <Col span={6}>
                            <Form.Item style={{ marginBottom: '0.5rem' }} label='Start Period'>
                                <PlatformDatePicker
                                    size='small'
                                    suffixIcon={
                                        <CalendarRange size='0.875rem' style={{ color: token.colorTextQuaternary }} />
                                    }
                                    placeholder={'Jan 1st...'}
                                    style={{ width: '100%' }}
                                    disabled={dateRangeDisabled}
                                    disabledDate={(current) => Boolean(endPeriod && current.isAfter(endPeriod, 'day'))}
                                    value={startPeriod}
                                    onChange={(date) =>
                                        updateDraft(component.id, {
                                            ...draft,
                                            query: {
                                                ...draft.query,
                                                datasetId: draft.query?.datasetId ?? '',
                                                metrics: draft.query?.metrics ?? [],
                                                dimensions: draft.query?.dimensions ?? [],
                                                dateRange: updateDateRangeBoundary(
                                                    draft.query?.dateRange,
                                                    'start',
                                                    date,
                                                ),
                                            },
                                        })
                                    }
                                />
                            </Form.Item>
                        </Col>
                        <Col span={6}>
                            <Form.Item style={{ marginBottom: '0.5rem' }} label='End Period'>
                                <PlatformDatePicker
                                    size='small'
                                    suffixIcon={
                                        <CalendarRange size='0.875rem' style={{ color: token.colorTextQuaternary }} />
                                    }
                                    placeholder={'Dec 31st...'}
                                    style={{ width: '100%' }}
                                    disabled={dateRangeDisabled}
                                    disabledDate={(current) =>
                                        Boolean(startPeriod && current.isBefore(startPeriod, 'day'))
                                    }
                                    value={endPeriod}
                                    onChange={(date) =>
                                        updateDraft(component.id, {
                                            ...draft,
                                            query: {
                                                ...draft.query,
                                                datasetId: draft.query?.datasetId ?? '',
                                                metrics: draft.query?.metrics ?? [],
                                                dimensions: draft.query?.dimensions ?? [],
                                                dateRange: updateDateRangeBoundary(draft.query?.dateRange, 'end', date),
                                            },
                                        })
                                    }
                                />
                            </Form.Item>
                        </Col>
                    </Row>
                )}
            </div>
        </>
    );
};

export default Overview;
